import type { PlatformType } from '@/types'

import type {
    NativePaymentMessage,
    NativePaymentOrder,
    NativePaymentPack,
    NativePaymentPlatform,
    NativePaymentSku,
    NativePurchaseCapabilities,
    NativePurchaseAndroidProduct,
    NativePurchaseIosProduct,
    NativePurchaseProduct,
    NativePurchaseResult,
    NativePurchaseStatus,
    NativePurchaseTransaction,
    NativePurchaseTransactionPayload,
} from './types'

export function getNativePaymentPlatform(platform: PlatformType): NativePaymentPlatform | undefined {
    return platform === 'ios' || platform === 'android' ? platform : undefined
}

type NativeProductPriceAndCurrency = {
    price: number
    currency: string
    localizedPrice: string
}

export function getNativeProductPriceAndCurrency(
    product: NativePurchaseProduct,
    platform: NativePaymentPlatform,
): NativeProductPriceAndCurrency {
    if (platform === 'android') {
        const androidProduct = product as NativePurchaseAndroidProduct

        return {
            price: androidProduct.priceAmountMicros / 1_000_000,
            currency: androidProduct.priceCurrencyCode,
            localizedPrice: androidProduct.price,
        }
    }

    const iosProduct = product as NativePurchaseIosProduct

    return {
        price: iosProduct.priceAmount,
        currency: iosProduct.currency,
        localizedPrice: iosProduct.localizedPrice,
    }
}

function compareNativeProductPrice(a: NativePaymentPack, b: NativePaymentPack) {
    return (
        getNativeProductPriceAndCurrency(a.product, a.platform).price -
        getNativeProductPriceAndCurrency(b.product, b.platform).price
    )
}

export function getNativeProductsBySku(products: NativePurchaseProduct[]) {
    return Object.fromEntries(products.map((product) => [product.sku, product]))
}

export function selectLowestCoveringNativePaymentPack({
    amount,
    platform,
    products,
    skus,
}: {
    amount: number
    platform: NativePaymentPlatform
    products: Record<string, NativePurchaseProduct>
    skus: NativePaymentSku[]
}) {
    return skus
        .flatMap((sku) => {
            const product = products[sku.sku]

            if (
                sku.platform !== platform ||
                !product ||
                product.available === false ||
                sku.playgamaCurrencyAmount < amount
            ) {
                return []
            }

            return [{ ...sku, product }]
        })
        .sort((a, b) => a.playgamaCurrencyAmount - b.playgamaCurrencyAmount || compareNativeProductPrice(a, b))[0]
}

export function selectExactNativePaymentPack({
    amount,
    platform,
    products,
    skus,
}: {
    amount: number
    platform: NativePaymentPlatform
    products: Record<string, NativePurchaseProduct>
    skus: NativePaymentSku[]
}) {
    return skus
        .flatMap((sku) => {
            const product = products[sku.sku]

            if (
                sku.platform !== platform ||
                !product ||
                product.available === false ||
                sku.playgamaCurrencyAmount !== amount
            ) {
                return []
            }

            return [{ ...sku, product }]
        })
        .sort((a, b) => compareNativeProductPrice(a, b))[0]
}

export function isNativePurchaseCapabilities(
    payload: NativePaymentMessage['payload'],
): payload is NativePurchaseCapabilities {
    if (!payload) {
        return false
    }

    return (
        'available' in payload &&
        'canMakePayments' in payload &&
        typeof payload.available === 'boolean' &&
        typeof payload.canMakePayments === 'boolean'
    )
}

export function isNativePurchaseProductsPayload(
    payload: NativePaymentMessage['payload'],
): payload is { products: NativePurchaseProduct[] } {
    return payload ? 'products' in payload && Array.isArray(payload.products) : false
}

export function isNativePurchaseUnfinishedPayload(
    payload: NativePaymentMessage['payload'],
): payload is { transactions: NativePurchaseTransactionPayload[] } {
    return payload ? 'transactions' in payload && Array.isArray(payload.transactions) : false
}

export function getNativePurchaseStatus(payload: NativePaymentMessage['payload'] | NativePurchaseResult | undefined) {
    if (!payload || !('status' in payload) || typeof payload.status !== 'string') {
        return undefined
    }

    return payload.status.toLowerCase() as NativePurchaseStatus
}

export function getNativePurchaseResult(payload: NativePaymentMessage['payload']) {
    if (!payload || typeof payload !== 'object') {
        return undefined
    }

    return payload as NativePurchaseResult
}

export function isSuccessfulNativePurchaseStatus(status: NativePurchaseStatus | undefined) {
    return status === 'purchased'
}

export function getNativePurchaseMessage(payload: NativePurchaseResult | undefined) {
    return payload?.message || payload?.error || payload?.description
}

export function normalizeNativePurchaseTransaction(
    payload: NativePurchaseTransactionPayload | undefined,
): NativePurchaseTransaction | undefined {
    const receipt = payload && getNativePurchaseReceipt(payload)

    if (!payload?.sku || !payload.transactionId || !receipt) {
        return undefined
    }

    return {
        sku: payload.sku,
        transactionId: payload.transactionId,
        receipt,
    }
}

export function getNativePaymentSource(order: NativePaymentOrder) {
    if (order?.type === 'disable-ad-order') {
        return 'adFree'
    }

    if (order?.type === 'order') {
        return 'game'
    }

    return 'shop'
}

export function getNativeVerificationErrorMessage(response: Response, bodyText: string | undefined, errors: string[]) {
    const status = [response.status, response.statusText].filter(Boolean).join(' ')
    const responseBody = bodyText?.trim()

    if (responseBody) {
        return `Native purchase verification failed (${status}): ${responseBody}`
    }

    return errors[0] || `Native purchase verification failed (${status})`
}

function getNativePurchaseReceipt(payload: NativePurchaseTransactionPayload) {
    return payload.receipt || payload.purchaseToken || payload.token
}
